import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { WINDOW } from '@df/dom/globals';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { NAntavo } from '../../../../module/antavo/antavo.interface';
import { AntavoService } from '../../../../module/antavo/antavo.service';
import { PwaNativeBridgeService } from '../../../../module/pwa/pwa-native-bridge.service';
import { PwaAccountBackComponent } from '../../common/back/pwa-account-back.component';

export namespace NAccountMissionDetails {
    export const componentId = 'account-mission-details';

    export interface IAccountMissionDetailsContent {
        title?: string;
        backButtonText?: string;
        doItButtonText?: string;
        doneButtonText?: string;
        progressTitle?: string;
        rewardTitle?: string;
        completedTitle?: string;
    }
}

@Component({
    selector: 'account-mission-details',
    templateUrl: './account-mission-details.html',
    styleUrls: ['./account-mission-details.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TranslationModule, PwaAccountBackComponent, ICmsClientModule, RouterLink, HrefComponent]
})
@ICmsComponent<AccountMissionDetailsComponent>({
    id: NAccountMissionDetails.componentId,
    block: {
        url: NAccountMissionDetails.componentId
    },
    onDemandOnly: true,
    name: 'Account Mission Details',
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            default: 'Account'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'backButtonText',
            title: 'Back Button Text',
            default: 'Back to Missions'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'doItButtonText',
            title: 'Do It Button Text',
            default: 'Let’s do it!'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'doneButtonText',
            title: 'Done Button Text',
            default: 'You completed this mission!'
        }
    ]
})
export class AccountMissionDetailsComponent extends UiComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NAccountMissionDetails.IAccountMissionDetailsContent;

    protected mission?: NAntavo.IChallenge;
    private route = inject(ActivatedRoute);
    private pwaNativeBridgeService = inject(PwaNativeBridgeService);
    private antavoService = inject(AntavoService);
    private router = inject(Router);
    private dfEventService = inject(DfEventService);
    private app = inject(App);
    private window = inject(WINDOW);

    override ngOnInit() {
        super.ngOnInit();
        this.mission = this.route.snapshot.data['mission'];
    }

    get isCompleted(): boolean {
        return this.mission ? this.antavoService.isMissionCompleted(this.mission) : false;
    }

    getProgressPercentage(current: number, threshold: number): number {
        if (threshold === 0) return 0;
        return Math.min((current / threshold) * 100, 100);
    }

    get hasPushNotificationTag(): boolean {
        return this.mission?.tags?.includes('allow_push_notification') ?? false;
    }

    /**
     * Gets dynamic progress data for the first tag that starts with "Post" or "post"
     * @returns Single progress data object or undefined if no matching tag found
     */
    getDynamicProgressData(): { limit: number; current: number; propertyName: string; displayName: string } | undefined {
        return this.mission ? this.antavoService.getDynamicProgressData(this.mission) : undefined;
    }

    requestPushNotificationPermission(): void {
        this.dfEventService.success(event => {
            if (event.id === 'enableAntavoPush') {
                this.router.navigate(['/account/my-missions']);
            }
        });
        this.pwaNativeBridgeService.allowNotification();
    }

    /**
     * Returns a link for the mission CTA.
     * - If the URL points to this store, return a trimmed in-app path.
     * - If the URL contains "insiders", append required params (?website=<siteId>&isapp=1).
     */
    getTrimmedMissionUrl(missionUrl?: string): string | undefined {
        if (!missionUrl) return undefined;

        const baseUrl = this.window.location.protocol + '//' + this.window.location.host;
        const siteId = this.app.dfConfig?.ms?.siteId as string | undefined;

        // Internal link -> trim base and return path
        if (baseUrl && missionUrl.startsWith(baseUrl)) {
            return missionUrl.substring(baseUrl.length);
        } else {
            // check if the url contains live url (valid for uat)
            const commonUrls = ['https://www.lemieux.com', 'https://lemieux.com', 'http://www.lemieux.com', 'http://lemieux.com'];
            const baseCommonUrls = commonUrls.find(url => missionUrl.includes(url));
            if (baseCommonUrls) {
                return missionUrl.substring(baseCommonUrls.length);
            }
        }

        // External link: add insiders params if needed
        if (missionUrl.toLowerCase().includes('insiders')) {
            try {
                const url = new URL(missionUrl);
                if (siteId) {
                    url.searchParams.set('website', siteId);
                }
                url.searchParams.set('isapp', '1');
                return url.toString();
            } catch {
                // missionUrl might be relative; fallback to appending params manually
                const sep = missionUrl.includes('?') ? '&' : '?';
                const params = `website=${encodeURIComponent(siteId || '')}&isapp=1`;
                return missionUrl + sep + params;
            }
        }

        // Default: return original URL
        return missionUrl;
    }
}
